import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { AutocompleteInput } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput';
import type { SearchEvent } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput.types';
import { Chip } from '@ipfabric/ui-components/Chip/Chip';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import type { FormFieldBaseProps } from '@ipfabric/ui-components/FormField/FormField.types';
import {
  BACKSPACE,
  isAnyEnter,
} from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import {
  type KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Objects rather than strings so picks arrive via `onOptionSelect`. The string
 * variant reports them through `onChange`, indistinguishable from typing.
 */
type Option = { label: string; value: string };

export type MultiTextAutocompleteInputProps = FormFieldBaseProps & {
  name?: string;
  onChange: (value: string[]) => void;
  /** Committed chips. Insertion order is preserved and never sorted. */
  value: string[];
  /** Names offered in the dropdown. Empty is a legitimate state. */
  suggestions?: string[];
  /**
   * Allow committing text that is not in `suggestions`. When false the field
   * behaves as a closed picker.
   */
  allowFreeText?: boolean;
  placeholder?: string;
  hasError?: boolean;
  dataTestId?: string;
  /**
   * True while `suggestions` are being fetched. Forwarded to AutocompleteInput,
   * which shows a spinner and blocks ArrowDown until the list arrives.
   */
  loading?: boolean;
};

/**
 * Multi-value input combining free text with autocomplete: type a value and
 * press Enter (or blur) to commit it as a chip, or pick one from the dropdown.
 * Unlike `MultiSelect` it accepts arbitrary text, so it works before any
 * suggestions exist.
 */
export const MultiTextAutocompleteInput = ({
  className,
  error,
  name,
  onChange,
  value,
  suggestions = [],
  allowFreeText = true,
  placeholder = 'Type a name and press Enter',
  hasError,
  dataTestId,
  loading,
  ...fieldProps
}: MultiTextAutocompleteInputProps) => {
  const inputId = useHtmlId('multiTextAutocompleteInput');
  const [draft, setDraft] = useState('');

  // Substring rather than prefix match: values are often namespaced
  // (`ipf:owner`), so the distinctive part sits at the end.
  const options = useMemo<Option[]>(() => {
    const query = draft.trim().toLowerCase();
    return suggestions
      .filter(
        (suggestion) =>
          !value.includes(suggestion) &&
          (query === '' || suggestion.toLowerCase().includes(query)),
      )
      .map((suggestion) => ({ label: suggestion, value: suggestion }));
  }, [suggestions, value, draft]);

  // `onSearch` needs a stable identity or its debounce timer reschedules every
  // keystroke. `options` already filters, so this just echoes the current list.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const searchOptions = useCallback(
    async ({ nextPageNumber }: SearchEvent) => ({
      content: optionsRef.current,
      currentPage: nextPageNumber,
      paginationLimit: optionsRef.current.length,
      totalPageCount: 1,
      totalRowsCount: optionsRef.current.length,
    }),
    [],
  );

  const addValue = (rawValue: string) => {
    const trimmed = rawValue.trim();
    // Drop blanks and duplicates here rather than failing on submit.
    if (trimmed === '' || value.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const removeValue = (removed: string) => {
    onChange(value.filter((item) => item !== removed));
  };

  const commitDraft = () => {
    if (!allowFreeText) {
      setDraft('');
      return;
    }
    addValue(draft);
  };

  /**
   * Capture phase so this runs before AutocompleteInput's own handler.
   * That handler selects the highlighted option on Enter; NIM-20936 also
   * re-highlights the first option on every keystroke, so a prefix like
   * "own" would otherwise become "owner". Picks are click-only.
   */
  const handleKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    if (isAnyEnter(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      commitDraft();
      return;
    }
    // Backspace on an empty draft removes the last chip.
    if (e.key === BACKSPACE && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <FormField
      className={className}
      error={error}
      htmlFor={inputId}
      {...fieldProps}
    >
      {/*
        Carries what AutocompleteInput does not forward: onKeyDown and onBlur.
        TextInput's public class slots keep this feature's chip layout local.
      */}
      <div onBlur={commitDraft} onKeyDownCapture={handleKeyDownCapture}>
        <AutocompleteInput.Record<Option>
          className='gap-4xs max-h-52 overflow-y-auto'
          dataTestId={dataTestId}
          disabled={fieldProps.disabled}
          editableWithTags
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.label}
          hasError={hasError || error !== undefined}
          // AutocompleteInput always supplies an onClear, so the clear-all
          // button must be suppressed explicitly. Removal is per-chip.
          hideClearButton
          // Free text is valid, so "No results" would wrongly imply otherwise.
          hideMenuWhenEmpty
          inputId={inputId}
          inputClassName='min-w-8'
          // Picked options drop out of `options`, so the list shrinks and
          // closes itself once empty.
          keepMenuOpenOnSelect
          isOptionEqualToValue={(option, val) => option.value === val}
          loading={loading}
          name={name}
          onChange={setDraft}
          onOptionSelect={(option) => addValue(option.value)}
          onSearch={searchOptions}
          options={options}
          // TextInput shows the placeholder alongside chips, where it reads as
          // a stale instruction. Empty state only.
          placeholder={value.length > 0 ? undefined : placeholder}
          renderOption={(option) => (
            <span
              data-testid={`multi-text-autocomplete-option-${option.value}`}
            >
              {option.label}
            </span>
          )}
          // Chips share the input's wrap context, so the caret follows the last
          // one instead of dropping to its own line.
          tagsContainerClassName='contents'
          renderTags={
            value.length > 0
              ? () =>
                  value.map((item) => (
                    <Chip
                      key={item}
                      dataTestId={`multi-text-autocomplete-chip-${item}`}
                      disabled={fieldProps.disabled}
                      onRemove={() => removeValue(item)}
                      // Size left unset so it falls through to Chip's 'default',
                      // matching MultiSelect (e.g. Excluded WAN interfaces field).
                      variant='outline'
                    >
                      {item}
                    </Chip>
                  ))
              : undefined
          }
          value={draft}
        />
      </div>
    </FormField>
  );
};
